<template>
  <div class="users-content">
    <table v-if="loaded && users && users.length > 0" class="table organizations-table">
      <thead>
        <tr>
          <th></th>
          <th class="name-col">Name</th>
          <th>Type</th>
          <th class="roles-th">Role</th>
          <th>Created</th>
          <th>Active</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="user in users"
          :key="user.user_id"
          :class="{
            selected: user.user_id === selectedUser?.user_id,
          }"
          @click="selectUser(user)"
        >
          <td style="width: 44px">
            <div class="avatar-container" id="profile-drop-down">
              <img
                v-if="user.avatar_url"
                class="rounded-circle"
                :src="user.avatar_url + '?' + Date.now()"
                alt="Avatar"
              />
              <div v-else class="img-placeholder">
                <i class="material-icons align-middle">account_circle</i>
              </div>
            </div>
          </td>

          <td class="name-col" style="width: 25%">
            <div class="user-name">{{ user.name }}</div>
            <div class="user-email">{{ user.username }}</div>
          </td>

          <td style="width: 25%">
            <div v-if="user.is_local_administrator">{{ 'Local Administrator' }}</div>
            <div v-else>Normal User</div>
          </td>

          <td style="width: 20%">
            <div v-if="user.role" class="role">
              {{ getRoleName(user.role) }}
            </div>
            <div v-else>Not assigned</div>
          </td>

          <td style="width: 20%">
            {{ formatDate(user.created, 'full', 'DD/MM/YYYY') }}
          </td>

          <td style="width: 10%">
            <div v-if="user.is_enabled">Active</div>
          </td>

          <td style="width: 64px">
            <div class="dropdown">
              <i
                class="material-icons-outlined align-middle more-buttons dropdown-toggle"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                >more_vert</i
              >
              <div class="dropdown-menu dropdown-menu-right">
                <button type="button" class="dropdown-item" @click="openDeleteUserModal(user)">
                  <i class="material-icons-outlined align-middle">delete</i>
                  Delete
                </button>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="no-data">No Users</div>

    <DeleteUserModal
      @closeModal="showDeleteUserModal = false"
      :user="selectedUser"
      v-if="showDeleteUserModal"
      @deleted="getUsers"
    />
  </div>
</template>

<script>
  import DeleteUserModal from '@/components/admin/DeleteUserModal.vue';

  import UserModal from '@/components/admin/OrganisationDetails/Users/UserModal.vue';
  import { ADMIN_USERS_REQUEST, USER_REQUEST } from '@/store/actions/user';
  import { globalFiltersMixin } from '@/helpers/mixins';

  export default {
    name: 'UsersList',

    components: {
      DeleteUserModal,
      UserModal,
    },

    props: {
      searchText: {
        type: String,
        default: '',
      },
      organisation: {
        type: Object,
        required: true,
      },
    },

    mixins: [globalFiltersMixin],

    data() {
      return {
        showDeleteUserModal: false,
        selectedUser: null,
        loaded: false,
      };
    },

    created() {
      this.getUsers();
    },

    computed: {
      users() {
        return this.$store.state.user.users.filter((user) => {
          if (!this.searchText) return true;

          const searchText = this.searchText.toLowerCase();

          const { name = '', username = '' } = user;
          return (
            !!name?.toLowerCase().includes(searchText) ||
            !!username?.toLowerCase().includes(searchText)
          );
        });
      },

      isAdmin() {
        return this.$store.getters.isAdministrator;
      },

      roles() {
        return this.$store.state.roles.roles;
      },
    },

    methods: {
      async getUsers() {
        if (this.isAdmin) {
          await this.$store.dispatch(ADMIN_USERS_REQUEST, this.organisation.organisation_id);
        } else {
          await this.$store.dispatch(USER_REQUEST);
        }
        if (!this.loaded) {
          this.loaded = true;
        }
      },

      selectUser(user) {
        if (!this.selectedUser) {
          this.selectedUser = user;
          this.$emit('userSelected', user);
          this.$emit('toggleSidebar', true);
        } else if (this.selectedUser.user_id === user.user_id) {
          this.selectedUser = null;
          this.$emit('toggleSidebar', false);
        } else {
          this.selectedUser = user;
          this.$emit('userSelected', user);
          this.$emit('toggleSidebar', true);
        }
      },

      openDeleteUserModal(organisation) {
        this.selectUser(organisation);
        this.showDeleteUserModal = true;
      },

      getRoleName(roleId) {
        return this.roles.find((role) => role.id === roleId)?.name;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .users-content {
    display: flex;
    width: 100%;
    padding-bottom: 0px;
  }

  .organizations-table {
    border: none;
    border-spacing: 0 8px;
    border-collapse: separate;
    margin-bottom: 0;

    table {
      width: 100%;
    }

    thead {
      th {
        position: sticky;
        top: 0;
        box-shadow: 0px 0px 0 8px $backgroundGrey;
        background-color: $backgroundGrey;
      }
    }

    th,
    td {
      border: none;
    }

    th {
      border: none;
      padding: 12px 15px;
      font-weight: 500;
      font-size: 14px;
      line-height: 21px;

      & + th {
        text-align: left;
      }
    }

    .roles-th {
      padding-left: 23px;
    }

    td {
      background: none;
      border-top: 2px solid transparent;
      border-bottom: 2px solid transparent;
      padding: 15px;
      height: auto;
      vertical-align: middle;
      font-family: Poppins;
      font-weight: 400;
      font-size: 16px;
      line-height: 100%;
      letter-spacing: 0%;

      .role {
        color: #6a6b6a;
        font-family: Whyte Inktrap;
        font-weight: 400;
        font-size: 12px;
        line-height: 100%;
        text-transform: uppercase;
        border: 1px solid #6a6b6a;
        border-radius: 50px;
        height: auto;
        width: fit-content;
        padding: 8px 8px 4px 8px;
        display: flex;
        align-items: center;
        text-align: center;
      }

      &:first-child {
        border-left: 2px solid transparent;
      }

      &:last-child {
        border-right: 2px solid transparent;
      }

      & + td {
        text-align: left;
      }
    }

    .name-col {
      text-align: left;
    }

    tbody {
      overflow-y: auto;
      height: 100%;

      tr {
        cursor: pointer;
        background-color: #fff;

        &:hover,
        &.selected {
          td {
            border-top: 2px solid $primaryRed;
            border-bottom: 2px solid $primaryRed;

            &:first-child {
              border-left: 2px solid $primaryRed;
            }

            &:last-child {
              border-right: 2px solid $primaryRed;
            }
          }
        }
      }
    }
  }

  .avatar-container img {
    width: 32px;
    height: 32px;
    background-color: $fillGrey;
    .img-placeholder {
      i {
        color: #2f6eae;
        font-size: 50px;
      }
    }
  }

  .user-name {
    font-family: Poppins;
    font-weight: 500;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0%;
  }

  .user-email {
    font-family: Poppins;
    font-weight: 400;
    font-size: 12px;
    line-height: 24px;
    letter-spacing: 0%;
    th,
    td {
      border: none;
    }
  }
</style>
